import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useCallback, useState } from 'react';

import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  SidebarMenuButton,
  useSidebar,
  useTheme,
  type IconType,
  type TTheme,
} from '@kognitos/lattice';
import { useAuth } from '@/shared/auth';
import { useCurrentUser } from '@/shared/auth/hooks/useCurrentUser';
import { useGetOrganization } from '@/generated/hooks/agora/v1/organization';
import { useIsOnboardingOrInvitationPage } from '@/routes/util';
import {
  openOrganizationSwitcher,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import UserDetailsSkeleton from './UserDetailsSkeleton';
import UserDetails from './UserDetails';
import DropdownMenuItems from './DropdownMenuItems';
import { getUserMenuItems } from './util';

const getThemeOptions = (
  t: ReturnType<typeof useTranslation>['t'],
): {
  value: TTheme;
  label: string;
  icon: IconType;
}[] =>
  [
    {
      value: 'light',
      label: t('theme.light'),
      icon: 'Sun',
    },
    {
      value: 'dark',
      label: t('theme.dark'),
      icon: 'Moon',
    },
    {
      value: 'system',
      label: t('theme.system'),
      icon: 'Monitor',
    },
  ] as const;

const UserDropdown = ({
  onDropdownOpenChange,
}: {
  onDropdownOpenChange?: (open: boolean) => void;
} = {}) => {
  const { user: authUser, logout, isAuthenticated } = useAuth();
  const { t: tNav } = useTranslation('navigation');
  const { t: tTheme } = useTranslation('theme');
  const { state: sidebarState } = useSidebar();
  const { theme, setTheme } = useTheme();
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { value: userOpen, toggle: toggleUser } = useBoolean();
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );

  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isError: isCurrentUserError,
  } = useCurrentUser({
    query: {
      enabled: isAuthenticated && !isOnboardingOrInvitationPage,
      retry: false,
    },
  });

  const { data: currentOrg, isLoading: isOrgLoading } = useGetOrganization(
    currentOrganizationId,
    {
      query: {
        enabled:
          !!currentOrganizationId &&
          isAuthenticated &&
          !isOnboardingOrInvitationPage,
        retry: false,
      },
    },
  );

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      toggleUser();
      if (!open) {
        setAppearanceOpen(false);
      }
      onDropdownOpenChange?.(open);
    },
    [toggleUser, onDropdownOpenChange],
  );

  const handleThemeChange = useCallback(
    (newTheme: TTheme) => {
      setTheme(newTheme);
      setAppearanceOpen(false);
      handleDropdownOpenChange(false);
    },
    [setTheme, handleDropdownOpenChange],
  );

  // Get menu items with proper callbacks
  const menuItems = getUserMenuItems({
    currentOrganizationId,
    currentWorkspaceId,
    onSelect: () => {
      handleDropdownOpenChange(false);
    },
    logout,
  });

  // Intercept the switch organization item to add the org switcher logic
  const menuItemsWithSwitchOrg = menuItems.map(item => {
    if (item.key === 'switch-organization') {
      return {
        ...item,
        onClick: () => {
          openOrganizationSwitcher();
          handleDropdownOpenChange(false);
        },
      };
    }
    return item;
  });

  // Handle error states
  if (isCurrentUserError) {
    return (
      <SidebarMenuButton
        size="lg"
        className="!transition-none text-destructive hover:text-destructive"
        disabled
      >
        <Icon type="AlertCircle" size="sm" className="text-destructive" />
        <Flex
          direction="column"
          align="start"
          className="flex-1 text-left gap-0.5 group-data-[collapsible=icon]:hidden"
        >
          <span className="text-xs font-medium leading-none">
            {tNav('navigation.errors.failedToLoadUserData')}
          </span>
        </Flex>
      </SidebarMenuButton>
    );
  }

  if (isCurrentUserLoading || isOrgLoading) {
    return <UserDetailsSkeleton />;
  }

  if (!isAuthenticated) {
    return null;
  }

  const userData = currentUser?.data.user;
  const orgData = currentOrg?.data;

  const userName = isOnboardingOrInvitationPage
    ? (authUser?.name ?? '')
    : (userData?.display_name ?? '');
  const organizationName = orgData?.title ?? '';

  // Split menu items to insert appearance submenu in the right place
  const itemsBeforeAppearance = menuItemsWithSwitchOrg.filter(
    item =>
      item.key !== 'separator-3' &&
      item.key !== 'profile' &&
      item.key !== 'logout' &&
      item.key !== 'separator-4' &&
      item.key !== 'switch-organization',
  );
  const itemsAfterAppearance = menuItemsWithSwitchOrg.filter(
    item =>
      item.key === 'separator-3' ||
      item.key === 'profile' ||
      item.key === 'logout' ||
      item.key === 'separator-4' ||
      item.key === 'switch-organization',
  );

  return (
    <DropdownMenu
      open={userOpen}
      onOpenChange={handleDropdownOpenChange}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          data-testid="user-dropdown-trigger"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-0 !transition-none"
        >
          <UserDetails name={userName} subtitle={organizationName} />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[248px]"
        sideOffset={sidebarState === 'collapsed' ? 16 : 12}
        onCloseAutoFocus={e => {
          // Prevent focus return to trigger to avoid focus ring artifacts
          e.preventDefault();
        }}
      >
        {/* Render items before appearance submenu */}
        <DropdownMenuItems items={itemsBeforeAppearance} />

        {/* Appearance submenu - special handling for expandable menu */}
        <DropdownMenuItem
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setAppearanceOpen(!appearanceOpen);
          }}
        >
          <Icon type="SunDim" size="sm" />
          {tNav('navigation.sidebar.appearance')}
          <Icon
            type="ChevronRight"
            className="ml-auto h-4 w-4 transition-transform duration-200"
            rotate={appearanceOpen ? 90 : undefined}
          />
        </DropdownMenuItem>
        <div
          className={cn(
            'overflow-hidden transition-all duration-200 ease-in-out',
            appearanceOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0',
          )}
        >
          {getThemeOptions(tTheme).map(option => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleThemeChange(option.value)}
              className="pl-8"
            >
              <Icon type={option.icon} size="sm" />
              {option.label}
              {theme === option.value && (
                <Icon type="Check" className="ml-auto h-4 w-4" />
              )}
            </DropdownMenuItem>
          ))}
        </div>

        {/* Render items after appearance submenu */}
        <DropdownMenuItems items={itemsAfterAppearance} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
